// Sign-up flow v2 — flow state + localStorage persistence, so an OAuth
// round-trip (Square/Clover/… send the browser away and back) resumes exactly
// where the owner left off. The password is never persisted.

import type { ToolKey, TypeKey } from './data';

export interface Person { first: string; last: string; email: string; phone: string; role: string; invited?: boolean }
export interface VendorRow { name: string; email: string; created?: boolean; from?: string }
export interface MenuLine { name: string; price: number | null; category?: string; pushed?: boolean }
export interface ChatMsg { r: 'u' | 'a'; t: string; fills?: string[]; tip?: number }
export interface ImportRowDone { label: string; detail: string }
export type ToolStatus = 'importing' | 'done' | 'call';
export interface Place { street: string; city: string; state: string; zip: string; phone: string; tz: string; hours: string; src: string | null }

export interface SignupState {
  step: number;
  name: string; email: string; pw: string;
  type: TypeKey | null; biz: string; site: string;
  locs: string | null; locOff: Record<number, boolean>;
  registered: boolean; completed: boolean;
  tools: Partial<Record<ToolKey, ToolStatus>>;
  /** Finished import rows per tool — real API results, persisted for Review. */
  imports: Partial<Record<ToolKey, ImportRowDone[]>>;
  alt: null | 'open' | 'reading' | 'done';
  altDetail: string | null;
  /** Items actually created on the server (POS import + file + website + paste pushes). */
  itemsImported: number;
  /** Real location names (Square sync) — never invented. */
  locationNames: string[];
  /** Gross cents per day from a real sales sync (Square) — drives the spark. */
  salesDays: number[];
  salesTotalCents: number | null; salesSource: string | null; salesLabel: string | null;
  faireCreated: number | null;
  /** Pasted/parsed menu lines still living client-side (pushed => on the server). */
  menu: MenuLine[];
  prices: Record<string, string>;
  place: Place;
  team: Person[]; invite: boolean; invitedCount: number;
  vendors: VendorRow[]; vendorsCreated: number;
  mods: string[] | null;
  call: string | null;
  doneTasks: Record<string, boolean>;
  chat: ChatMsg[];
  savedAt?: number;
}

export const KEY = 'mise-signup-v2';

export const FRESH: SignupState = {
  step: 0,
  name: '', email: '', pw: '',
  type: null, biz: '', site: '',
  locs: null, locOff: {},
  registered: false, completed: false,
  tools: {}, imports: {},
  alt: null, altDetail: null,
  itemsImported: 0, locationNames: [], salesDays: [],
  salesTotalCents: null, salesSource: null, salesLabel: null,
  faireCreated: null,
  menu: [], prices: {},
  place: { street: '', city: '', state: '', zip: '', phone: '', tz: '', hours: '', src: null },
  team: [], invite: true, invitedCount: 0,
  vendors: [], vendorsCreated: 0,
  mods: null, call: null, doneTasks: {}, chat: [],
};

export function load(): SignupState {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (v && typeof v === 'object') {
      // An in-flight import can't survive a reload — the real calls are gone.
      const tools: SignupState['tools'] = { ...(v.tools || {}) };
      (Object.keys(tools) as ToolKey[]).forEach((k) => { if (tools[k] === 'importing') delete tools[k]; });
      return {
        ...FRESH,
        ...v,
        pw: '',
        tools,
        alt: v.alt === 'reading' ? 'open' : v.alt ?? null,
        place: { ...FRESH.place, ...(v.place || {}) },
      };
    }
  } catch { /* corrupted state → fresh start */ }
  return { ...FRESH };
}

export function save(s: SignupState) {
  try {
    const { pw: _pw, ...rest } = s;
    localStorage.setItem(KEY, JSON.stringify({ ...rest, savedAt: Date.now() }));
  } catch { /* storage full/blocked — the flow still works, it just won't resume */ }
}

export function clear() {
  try { localStorage.removeItem(KEY); } catch { /* nothing to do */ }
}

/** Is a registered-but-unfinished signup sitting in storage? (Used by the
 * OAuth return catcher to steer /settings?tab=integrations back into the flow.) */
export function pendingSignupAtConnect(): boolean {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || 'null');
    return !!(v && v.registered && !v.completed && v.step === 2 && Date.now() - (v.savedAt || 0) < 60 * 60 * 1000);
  } catch { return false; }
}
